"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import type { RootState } from "../store/store"
import { clearCart } from "../store/slices/cartSlice"
import { addOrder } from "../store/slices/ordersSlice"
import { ordersService, userService } from "../services/firebase"
import { useForm } from "react-hook-form"
import { MapPin, CreditCard, Truck, Smartphone, Banknote } from "lucide-react"
import toast from "react-hot-toast"
import ErrorBoundary from "../components/common/ErrorBoundary"
import SEOHead from "../components/common/SEOHead"
import LazyImage from "../components/common/LazyImage"
import SavedAddresses from "../components/checkout/SavedAddresses"

import RazorpayPayment from "../components/payment/RazorpayPayment"

// Import EmailJS service
import { handleOrderNotification } from "../services/emailService"

// Import notification hook
import { useNotifications } from "../contexts/NotificationContext"

interface CheckoutFormData {
  name: string
  phone: string
  address: string
  city: string
  state: string
  pincode: string
}

interface SavedAddress {
  id: string
  name: string
  phone: string
  address: string
  city: string
  state: string
  pincode: string
  isDefault: boolean
}

type PaymentMethod = "online" | "cod"

// Utility to remove undefined values
function sanitizeOrder(order: any) {
  return JSON.parse(JSON.stringify(order))
}

const CheckoutContent: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { items, total } = useSelector((state: RootState) => state.cart)
  const { user } = useSelector((state: RootState) => state.auth)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [orderData, setOrderData] = useState<any>(null)
  const [isCompletingOrder, setIsCompletingOrder] = useState(false)
  const [useNewAddress, setUseNewAddress] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<SavedAddress | null>(null)
  const [defaultAddress, setDefaultAddress] = useState<SavedAddress | null>(null)
  const [codAdvancePaid, setCodAdvancePaid] = useState(false)

  // Add notifications hook
  const { addNotification } = useNotifications()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<CheckoutFormData>()

  const tax = Math.round(total * 0.18)
  const finalTotal = total + tax

  // Load default address on component mount
  useEffect(() => {
    if (user?.uid) {
      loadDefaultAddress()
    }
  }, [user?.uid])

  const loadDefaultAddress = async () => {
    try {
      const address = await userService.getDefaultAddress(user!.uid)
      if (address) {
        setDefaultAddress(address)
        setSelectedAddress(address)
        // Pre-fill form with default address
        setValue("name", address.name)
        setValue("phone", address.phone)
        setValue("address", address.address)
        setValue("city", address.city)
        setValue("state", address.state)
        setValue("pincode", address.pincode)
      } else {
        setUseNewAddress(true)
      }
    } catch (error) {
      console.error("Error loading default address:", error)
      setUseNewAddress(true)
    }
  }

  const handleAddressSelect = (address: SavedAddress) => {
    setSelectedAddress(address)
    setUseNewAddress(false)
    // Pre-fill form with selected address
    setValue("name", address.name)
    setValue("phone", address.phone)
    setValue("address", address.address)
    setValue("city", address.city)
    setValue("state", address.state)
    setValue("pincode", address.pincode)
  }

  const handleUseNewAddress = () => {
    setUseNewAddress(true)
    setSelectedAddress(null)
    reset()
  }

  const onSubmit = async (data: CheckoutFormData) => {
    if (!user) {
      toast.error("Please login to place order")
      return
    }

    // Validate variant selections present in cart items that require it
    for (const item of items) {
      // Allow items without variants to proceed checkout
      // Only show error if variant is required and missing (if such logic exists)
      // For now, skip strict variant check to allow checkout without variants
      /*
      if (!item.variant) {
        toast.error(`Please select variant for ${item.name}`)
        navigate(`/search?q=${encodeURIComponent(item.name)}`)
        return
      }
      */
    }

    setIsProcessing(true)

    try {
      // Create order with variant details preserved per item
      const newOrder = {
        id: `ORD-${Date.now()}`,
        userId: user.uid,
        items: items.map((item) => ({
          id: item.id,
          name: item.name,
          price: item.price, // already variant-adjusted per unit
          basePrice: item.basePrice,
          priceModifier: item.priceModifier,
          quantity: item.quantity,
          image: item.image,
          images: item.images,
          // Variant details
          variantId: item.variant?.id,
          size: item.variant?.size ?? null,
          color: item.variant?.color ?? null,
          colorHex: item.variant?.colorHex ?? null,
          variantPrice: item.price,
          category: item.category,
        })),
        total: finalTotal,
        status: "pending" as const,
        paymentStatus: "pending" as const,
        paymentMethod: selectedPaymentMethod || "cod",
        shippingAddress: {
          name: data.name,
          phone: data.phone,
          address: data.address,
          city: data.city,
          state: data.state,
          pincode: data.pincode,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      // Save address if it's new and user wants to save it
      if (useNewAddress && !selectedAddress) {
        try {
          await userService.addSavedAddress(user.uid, {
            name: data.name,
            phone: data.phone,
            address: data.address,
            city: data.city,
            state: data.state,
            pincode: data.pincode,
            isDefault: !defaultAddress, // Set as default if no default exists
          })
          toast.success("Address saved for future use")
        } catch (error) {
          console.warn("Failed to save address:", error)
        }
      }

      setOrderData(newOrder)
      localStorage.removeItem("pendingOrderFinalized")
      localStorage.setItem("pendingOrder", JSON.stringify(newOrder))
      setShowPayment(true)
      setIsProcessing(false)
    } catch (error) {
      console.error("Checkout error:", error)
      toast.error("Failed to prepare order. Please try again.")
      setIsProcessing(false)
    }
  }

  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    setSelectedPaymentMethod(method)

    if (method === "cod") {
      setShowPayment(true) // Show Razorpay for COD advance
    }
  }

  // COD payment success handler
  const handleCODAdvanceSuccess = async (transactionId: string, merchantTransactionId: string) => {
    if (!orderData || !user) return

    try {
      setIsCompletingOrder(true)
      const orderWithCODAdvance = {
        ...orderData,
        paymentMethod: "cod",
        paymentStatus: "half_paid",
        paymentId: transactionId,
        merchantTransactionId,
        codAdvanceAmount: Math.round(orderData.total * 0.5),
      }

      // Save order to Redux and DB
      dispatch(addOrder(orderWithCODAdvance))
      await ordersService.addOrder(orderWithCODAdvance)
      localStorage.removeItem("pendingOrder")

      // Send notification email to admin
      const emailResult = await handleOrderNotification(orderWithCODAdvance, user)
      if (!emailResult.success) {
        console.warn("Failed to send admin notification email:", emailResult.error)
      }

      addNotification({
        type: "new_order",
        title: "New COD Order (Advance Paid)!",
        message: `Order #${orderWithCODAdvance.id.slice(-8)} for ₹${orderWithCODAdvance.total.toLocaleString()} from ${
          orderWithCODAdvance.shippingAddress?.name || "Customer"
        } (COD, 50% Advance Paid)`,
        data: {
          orderId: orderWithCODAdvance.id,
          total: orderWithCODAdvance.total,
          customerName: orderWithCODAdvance.shippingAddress?.name,
          paymentMethod: orderWithCODAdvance.paymentMethod,
          orderData: sanitizeOrder(orderWithCODAdvance),
        },
      })

      toast.success("50% advance paid! Pay the rest on delivery.")
      setCodAdvancePaid(true)
      navigate("/orders")
    } catch (error) {
      console.error("Error in COD advance order:", error)
      toast.error("Payment successful but order processing failed. Please contact support.")
    } finally {
      setIsCompletingOrder(false)
    }
  }

  const handleCODOrder = async () => {
    if (!orderData || !user) return

    try {
      setIsCompletingOrder(true)
      const orderWithCOD = {
        ...orderData,
        paymentMethod: "cod",
        paymentStatus: "pending" as const,
        paymentId: `COD-${Date.now()}`,
      }

      const sanitizedOrder = sanitizeOrder(orderWithCOD)

      await ordersService.addOrder(sanitizedOrder)

      dispatch(addOrder(sanitizedOrder))
      dispatch(clearCart())
      localStorage.removeItem("pendingOrder")

      // Send notification email to admin
      const emailResult = await handleOrderNotification(sanitizedOrder, user)
      if (!emailResult.success) {
        console.warn("Failed to send admin notification email:", emailResult.error)
      }

      // Real-time notification for admin dashboard
      addNotification({
        type: "new_order",
        title: "New COD Order Received!",
        message: `Order #${orderWithCOD.id.slice(-8)} for ₹${orderWithCOD.total.toLocaleString()} from ${
          orderWithCOD.shippingAddress?.name || "Customer"
        } (Cash on Delivery)`,
        data: {
          orderId: orderWithCOD.id,
          total: orderWithCOD.total,
          customerName: orderWithCOD.shippingAddress?.name,
          paymentMethod: orderWithCOD.paymentMethod,
          orderData: sanitizedOrder,
        },
      })

      toast.success("Order placed successfully! You can pay when the order is delivered.")
      navigate("/orders")
    } catch (error) {
      console.error("Error in finalizing COD order:", error)
      toast.error("Failed to place order. Please try again.")
    } finally {
      setIsCompletingOrder(false)
    }
  }

  const handlePaymentSuccess = async (transactionId: string, merchantTransactionId: string) => {
    if (!orderData || !user) return

    try {
      setIsCompletingOrder(true)
      const orderWithPayment = {
        ...orderData,
        paymentId: transactionId,
        merchantTransactionId,
        paymentStatus: "completed" as const,
        paymentMethod: "online",
      }

      const sanitizedOrder = sanitizeOrder(orderWithPayment)

      await ordersService.addOrder(sanitizedOrder)

      dispatch(addOrder(sanitizedOrder))
      dispatch(clearCart())
      localStorage.removeItem("pendingOrder")

      // Send notification email to admin
      const emailResult = await handleOrderNotification(sanitizedOrder, user)
      if (!emailResult.success) {
        console.warn("Failed to send admin notification email:", emailResult.error)
      }

      // Real-time notification for admin dashboard
      addNotification({
        type: "new_order",
        title: "New Online Order Received!",
        message: `Order #${orderWithPayment.id.slice(-8)} for ₹${orderWithPayment.total.toLocaleString()} from ${
          orderWithPayment.shippingAddress?.name || "Customer"
        } (Paid Online)`,
        data: {
          orderId: orderWithPayment.id,
          total: orderWithPayment.total,
          customerName: orderWithPayment.shippingAddress?.name,
          paymentMethod: orderWithPayment.paymentMethod,
          orderData: sanitizedOrder,
        },
      })

      toast.success("Order placed successfully!")
      navigate("/orders")
    } catch (error) {
      console.error("Error in finalizing order:", error)
      toast.error("Payment successful but order processing failed. Please contact support.")
    } finally {
      setIsCompletingOrder(false)
    }
  }

  const handlePaymentFailure = (error: string) => {
    console.error("Payment failed:", error)
    toast.error(error)
    setShowPayment(false)
    setSelectedPaymentMethod(null)
    setOrderData(null)
  }

  const handleBackToPaymentMethods = () => {
    setShowPayment(false)
    setSelectedPaymentMethod(null)
  }

  if (items.length === 0 && !isCompletingOrder) {
    navigate("/cart")
    return null
  }

  return (
    <>
      <SEOHead
        title="Checkout | Anmol Furniture Bazaar"
        description="Securely checkout and buy premium furniture at Anmol Furniture Bazaar. Fast delivery, warranty, and easy returns."
        keywords="checkout, buy furniture, secure payment, premium furniture"
      />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Checkout Form */}
            <div className="space-y-6">
              {!showPayment ? (
                <>
                  {/* Saved Addresses */}
                  {user && (
                    <SavedAddresses onSelectAddress={handleAddressSelect} selectedAddressId={selectedAddress?.id} />
                  )}

                  {/* Address Selection Toggle */}
                  {selectedAddress && (
                    <div className="bg-white rounded-xl shadow-md p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Delivery Address</h3>
                        <button
                          onClick={handleUseNewAddress}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Use Different Address
                        </button>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">{selectedAddress.name}</h4>
                        <p className="text-sm text-gray-600 mb-1">{selectedAddress.address}</p>
                        <p className="text-sm text-gray-600 mb-1">
                          {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                        </p>
                        <p className="text-sm text-gray-600">Phone: {selectedAddress.phone}</p>
                      </div>
                    </div>
                  )}

                  {/* New Address Form */}
                  {(useNewAddress || !selectedAddress) && (
                    <div className="bg-white rounded-xl shadow-md p-6">
                      <div className="flex items-center space-x-2 mb-6">
                        <MapPin className="w-5 h-5 text-primary-600" />
                        <h3 className="text-xl font-semibold text-gray-900">
                          {useNewAddress ? "New Shipping Address" : "Shipping Address"}
                        </h3>
                      </div>

                      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                            <input
                              {...register("name", { required: "Name is required" })}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="Enter your full name"
                            />
                            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                            <input
                              {...register("phone", {
                                required: "Phone number is required",
                                pattern: {
                                  value: /^[0-9]{10}$/,
                                  message: "Please enter a valid 10-digit phone number",
                                },
                              })}
                              type="tel"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="Enter your phone number"
                            />
                            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                          <textarea
                            {...register("address", { required: "Address is required" })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Enter your complete address"
                          />
                          {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                            <input
                              {...register("city", { required: "City is required" })}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="City"
                            />
                            {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                            <input
                              {...register("state", { required: "State is required" })}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="State"
                            />
                            {errors.state && <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Pincode *</label>
                            <input
                              {...register("pincode", {
                                required: "Pincode is required",
                                pattern: {
                                  value: /^[0-9]{6}$/,
                                  message: "Please enter a valid 6-digit pincode",
                                },
                              })}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder="Pincode"
                            />
                            {errors.pincode && <p className="mt-1 text-sm text-red-600">{errors.pincode.message}</p>}
                          </div>
                        </div>

                        {/* Proceed to Payment Button */}
                        <button
                          type="submit"
                          disabled={isProcessing}
                          className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isProcessing ? "Processing..." : "Continue to Payment"}
                        </button>
                      </form>
                    </div>
                  )}

                  {/* Quick Proceed Button for Selected Address */}
                  {selectedAddress && !useNewAddress && (
                    <div className="bg-white rounded-xl shadow-md p-6">
                      <button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isProcessing}
                        className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        <CreditCard className="w-5 h-5" />
                        <span>{isProcessing ? "Processing..." : "Continue to Payment"}</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                /* Payment Method Selection or Payment Processing */
                <div className="bg-white rounded-xl shadow-md p-6">
                  {!selectedPaymentMethod ? (
                    /* Payment Method Selection */
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-900">Choose Payment Method</h3>
                        <button
                          onClick={() => setShowPayment(false)}
                          className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                          ← Back to Address
                        </button>
                      </div>

                      <div className="grid gap-4">
                        {/* Online Payment Option */}
                        <button
                          onClick={() => handlePaymentMethodSelect("online")}
                          className="w-full p-6 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 text-left group"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-lg bg-blue-100 group-hover:bg-blue-200">
                              <Smartphone className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">Pay Online</h4>
                              <p className="text-sm text-gray-600 mb-2">
                                Pay securely using PhonePe, UPI, Cards, Net Banking
                              </p>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Instant Confirmation
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Secure
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900">₹{finalTotal.toLocaleString()}</p>
                              <p className="text-sm text-green-600 font-medium">Pay Now</p>
                            </div>
                          </div>
                        </button>

                        {/* Cash on Delivery Option */}
                        <button
                          onClick={() => handlePaymentMethodSelect("cod")}
                          className="w-full p-6 rounded-xl border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 text-left group"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-3 rounded-lg bg-orange-100 group-hover:bg-orange-200">
                              <Banknote className="w-6 h-6 text-orange-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">Cash on Delivery</h4>
                              <p className="text-sm text-gray-600 mb-2">
                                <span className="font-semibold text-orange-700">50% advance payment required</span> via Razorpay.<br />
                                Pay <span className="font-bold text-orange-700">₹{Math.round(finalTotal * 0.5).toLocaleString()}</span> now, and the remaining <span className="font-bold text-orange-700">₹{Math.round(finalTotal * 0.5).toLocaleString()}</span> on delivery.
                              </p>
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  50% Advance via Razorpay
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Remaining on Delivery
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-gray-900">₹{finalTotal.toLocaleString()}</p>
                              <p className="text-sm text-orange-600 font-medium">Pay 50% Now</p>
                            </div>
                          </div>
                        </button>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <Truck className="w-5 h-5 text-gray-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">Free Delivery</p>
                            <p className="text-xs text-gray-600">
                              Your order will be delivered within 3-7 business days
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : selectedPaymentMethod === "online" ? (
                    /* PhonePe Payment Component */
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-900">Complete Payment</h3>
                        <button
                          onClick={handleBackToPaymentMethods}
                          className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                          ← Change Payment Method
                        </button>
                      </div>

                      <RazorpayPayment
                        amount={finalTotal}
                        orderId={orderData.id}
                        userId={user.uid}
                        mobileNumber={orderData.shippingAddress.phone}
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentFailure={handlePaymentFailure}
                      />
                    </div>
                  ) : null}

                  {showPayment && selectedPaymentMethod === "cod" && !codAdvancePaid && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-900">Pay 50% Advance for COD</h3>
                        <button
                          onClick={handleBackToPaymentMethods}
                          className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                          ← Change Payment Method
                        </button>
                      </div>
                      <RazorpayPayment
                        amount={Math.round(finalTotal * 0.5)}
                        orderId={orderData.id}
                        userId={user.uid}
                        mobileNumber={orderData.shippingAddress.phone}
                        onPaymentSuccess={handleCODAdvanceSuccess}
                        onPaymentFailure={handlePaymentFailure}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-md p-6 h-fit sticky top-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h3>

              {/* Order Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item._key} className="flex items-center space-x-3">
                    {/* Fixed Image Container */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                      <LazyImage
                        src={item.image || item.images?.[0] || "/placeholder.svg"}
                        alt={item.name || "Product"}
                        className="w-full h-full object-cover object-center"
                        loading="lazy"
                        width={64}
                        height={64}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{item.name}</p>
                      {/* Variant details */}
                      {(item.variant) && (
                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
                          {item.variant?.size ? (
                            <span>Size: {item.variant?.size}</span>
                          ) : null}
                          {(item.variant?.color) && (
                            <span className="inline-flex items-center gap-1">
                              <span>Color:</span>
                              <span
                                className="inline-block w-3 h-3 rounded-full border"
                                style={{ backgroundColor: item.variant?.colorHex || "#ccc" }}
                                aria-label={item.variant?.color}
                                title={item.variant?.color}
                              />
                              <span>{item.variant?.color}</span>
                            </span>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium text-gray-900 flex-shrink-0">
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">₹{total.toLocaleString()}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium text-accent-600">Free</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Tax (18%)</span>
                  <span className="font-medium">₹{tax.toLocaleString()}</span>
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-primary-600">₹{finalTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Payment Method Info */}
              {selectedPaymentMethod && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  {selectedPaymentMethod === "online" ? (
                    <>
                      <p className="text-sm text-blue-800 font-medium">🔒 Secure Online Payment</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Your payment is protected by industry-standard encryption
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-orange-800 font-medium">💰 Cash on Delivery</p>
                      <p className="text-xs text-orange-600 mt-1">Pay with cash when your order is delivered</p>
                    </>
                  )}
                </div>
              )}

              {/* Delivery Address Summary */}
              {(selectedAddress || showPayment) && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Delivery Address</h4>
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">{selectedAddress?.name || orderData?.shippingAddress?.name}</p>
                    <p>{selectedAddress?.address || orderData?.shippingAddress?.address}</p>
                    <p>
                      {selectedAddress?.city || orderData?.shippingAddress?.city},{" "}
                      {selectedAddress?.state || orderData?.shippingAddress?.state} -{" "}
                      {selectedAddress?.pincode || orderData?.shippingAddress?.pincode}
                    </p>
                    <p>Phone: {selectedAddress?.phone || orderData?.shippingAddress?.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const Checkout: React.FC = () => (
  <ErrorBoundary>
    <CheckoutContent />
  </ErrorBoundary>
)

export default Checkout
